import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'Quanturama | Automation, Trading Bots & Web3 Infrastructure',
  description = 'Quanturama builds automation systems, trading bots, and Web3 infrastructure for protocols, startups, and operators. Project-based. Retainer. Fractional.',
  keywords = 'trading bot development, CEX DEX automation, Web3 infrastructure, DApp development, smart contract developer, AI agent system, crypto automation, hummingbot developer, automation pipeline, blockchain operator, Web3 ecosystem growth, systems diagnosis, token economy, multi-agent systems',
  author = 'Quanturama',
  image = '/assets/Qunaturama-logo.png',
  url = 'https://quanturama.com',
  type = 'website'
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Quanturama",
    "description": "Web3 and automation operator. Builds trading bots, automation pipelines, AI agent systems, and decentralized infrastructure for protocols, startups, and operators.",
    "url": "https://quanturama.com",
    "logo": "https://quanturama.com/assets/Qunaturama-logo.png",
    "email": "hello@quanturama.com",
    "areaServed": "Worldwide",
    "knowsAbout": [
      "Web3", "DeFi", "blockchain", "smart contracts",
      "trading bots", "Hummingbot", "Freqtrade", "market making",
      "CEX arbitrage", "DEX arbitrage", "algorithmic trading",
      "automation pipelines", "AI agents", "multi-agent systems",
      "decentralized applications", "Polygon", "BSC", "TON",
      "prediction markets", "token economies", "ecosystem growth",
      "systems diagnosis", "web3 infrastructure"
    ],
    "sameAs": [
      "https://www.linkedin.com/company/quanturama/",
      "https://blog.quanturama.com",
      "https://t.me/QSatCryptoSignal",
      "https://t.me/QuanturamaLobby",
      "https://instagram.com/quanturama"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "hello@quanturama.com",
      "contactType": "Customer Service"
    }
  };

  const serviceSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Automation Pipelines and Trading Bots",
      "provider": { "@type": "Organization", "name": "Quanturama", "url": "https://quanturama.com" },
      "description": "Custom trading bots for CEX and DEX, workflow automation pipelines, and AI agent systems for operators and startups.",
      "areaServed": "Worldwide"
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Web3 Ecosystem Growth",
      "provider": { "@type": "Organization", "name": "Quanturama", "url": "https://quanturama.com" },
      "description": "Ecosystem strategy, community activation, and growth architecture for Web3 protocols and token economies.",
      "areaServed": "Worldwide"
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Decentralized Infrastructure",
      "provider": { "@type": "Organization", "name": "Quanturama", "url": "https://quanturama.com" },
      "description": "DApp development, smart contracts, SIWE authentication, and testnet-to-mainnet deployment on Polygon, BSC, TON.",
      "areaServed": "Worldwide"
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Systems Diagnosis",
      "provider": { "@type": "Organization", "name": "Quanturama", "url": "https://quanturama.com" },
      "description": "Architecture review, system redesign, and strategic depth for founders and operators who built something real and hit a wall.",
      "areaServed": "Worldwide"
    }
  ];

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Quanturama Blog",
    "url": "https://blog.quanturama.com",
    "description": "Research and essays on Web3 systems, automation, trading architecture, and AI agents.",
    "publisher": { "@type": "Organization", "name": "Quanturama", "url": "https://quanturama.com" }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Quanturama",
    "url": "https://quanturama.com"
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Quanturama" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      <meta property="twitter:creator" content="@quanturama" />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="theme-color" content="#8b5cf6" />
      <link rel="canonical" href={url} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(blogSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
